import axios from 'axios';
import { api } from '../../../shared/lib/axios';
import { unwrapArray, unwrapObject, unwrapPaginated, type Paginated } from './envelope';

export type PurchaseRequestStatus =
  | 'draft'
  | 'pending_approval'
  | 'returned'
  | 'approved'
  | 'rejected'
  | 'in_quotation'
  | 'pending_selection_approval'
  | 'finance_pending'
  | 'payment_in_progress'
  | 'paid'
  | 'closed'
  | 'cancelled';

export type PurchaseCriticality = 'low' | 'medium' | 'high' | 'critical';
export type PurchasePriority = 'low' | 'normal' | 'high';
export type PurchaseAcquisitionType = 'product' | 'service';

export interface NamedReference {
  id: string;
  name?: string;
  code?: string;
  corporateName?: string;
  tradeName?: string | null;
  razaoSocial?: string;
  nomeFantasia?: string | null;
  purchaseEligible?: boolean;
  clientId?: string | null;
}

export interface PurchaseAccessNeeds {
  data: boolean;
  systems: boolean;
  environments: boolean;
  internalInformation: boolean;
  details?: string | null;
}

export interface PurchaseRequestItem {
  id: string;
  lineNumber: number;
  description: string;
  quantity: number;
  unit: string;
  estimatedUnitPrice?: number | string | null;
  specifications?: string | null;
}

export interface PurchaseRequestAttachment {
  id: string;
  fileName: string;
  mimeType?: string | null;
  size?: number | null;
  fileSize?: number | null;
  sizeBytes?: number | string | null;
  kind?: string | null;
  description?: string | null;
  createdAt?: string;
  uploadedAt?: string;
}

export interface PurchaseQuotationItem {
  id?: string;
  purchaseRequestItemId: string;
  quantity: number;
  unitPrice: number;
  totalValue?: number;
  deliveryDays?: number | null;
  notes?: string | null;
  comparable?: boolean;
  nonComparableReason?: string | null;
}

export interface PurchaseQuotation {
  id: string;
  purchaseRequestId: string;
  supplierId: string;
  supplier?: NamedReference | null;
  quotedValue: number | string;
  currency: string;
  proposalValidUntil?: string | null;
  deliveryDays?: number | null;
  paymentTerms?: string | null;
  commercialNotes?: string | null;
  technicalNotes?: string | null;
  taxesAmount?: number | string | null;
  freightAmount?: number | string | null;
  notes?: string | null;
  status: 'draft' | 'submitted' | 'selected' | 'rejected';
  proposalFileUrl?: string | null;
  items: PurchaseQuotationItem[];
  availableActions?: string[];
  createdAt?: string;
}

export interface PurchaseQuotationException {
  id?: string;
  reason: string;
  justification: string;
  createdAt?: string;
  actor?: { id?: string; name?: string; email?: string } | null;
}

export interface PurchaseApproval {
  id: string;
  purchaseRequestId?: string;
  stepKey: string;
  stepLabel?: string;
  featureKey?: string;
  decision?: 'pending' | 'approved' | 'rejected' | 'returned' | 'pendente' | 'aprovado' | 'reprovado' | 'devolvido';
  status?: string;
  justification?: string | null;
  actorName?: string | null;
  decidedAt?: string | null;
  createdAt?: string;
  sortOrder?: number;
}

export interface PurchaseApprovalQueueEntry {
  request: PurchaseRequest;
  currentApproval: Pick<PurchaseApproval, 'id' | 'stepKey' | 'stepLabel'> & { phase?: string };
}

export interface PurchaseTimelineEntry {
  id: string;
  event?: string;
  type?: string;
  title?: string;
  description?: string | null;
  detail?: string | null;
  actorName?: string | null;
  actorRole?: string | null;
  createdAt?: string;
  eventAt?: string;
  metadata?: Record<string, unknown> | null;
}

export interface PurchasePayment {
  id: string;
  purchaseRequestId: string;
  status: 'pending' | 'scheduled' | 'paid' | 'reconciled' | 'cancelled' | string;
  dueDate: string;
  amount?: number | string | null;
  scheduledFor?: string | null;
  paidAt?: string | null;
  paymentReference?: string | null;
  reconciledAt?: string | null;
  reconciliationReference?: string | null;
  proofFileName?: string | null;
  proofUrl?: string | null;
  availableActions?: string[];
  createdAt?: string;
}

export interface PurchaseRequest {
  id: string;
  requestNumber?: string;
  code?: string;
  requesterUserId: string;
  requester?: { id?: string; name?: string; email?: string } | null;
  requesterUser?: { id?: string; name?: string; email?: string } | null;
  requestingArea: string;
  costCenterId?: string | null;
  costCenter?: NamedReference | null;
  costCenterRef?: NamedReference | null;
  legacyCostCenter?: string | null;
  clientId?: string | null;
  client?: NamedReference | null;
  projectId?: string | null;
  project?: NamedReference | null;
  title: string;
  justification: string;
  category: string;
  acquisitionType: PurchaseAcquisitionType;
  priority: PurchasePriority;
  estimatedValue?: number | string | null;
  neededBy?: string | null;
  scope: string;
  technicalRequirements?: string | null;
  functionalRequirements?: string | null;
  criticality: PurchaseCriticality;
  budgetStatus?: string | null;
  impact?: string | null;
  accessNeeds: PurchaseAccessNeeds;
  status: PurchaseRequestStatus;
  items: PurchaseRequestItem[];
  attachments: PurchaseRequestAttachment[];
  quotations?: PurchaseQuotation[];
  approvals?: PurchaseApproval[];
  payments?: PurchasePayment[];
  timeline?: PurchaseTimelineEntry[];
  availableActions?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreatePurchaseRequestItemPayload {
  description: string;
  quantity: number;
  unit: string;
  estimatedUnitPrice?: number;
  specifications?: string;
}

export interface CreatePurchaseRequestPayload {
  requestingArea: string;
  costCenterId: string;
  clientId?: string;
  projectId?: string;
  title: string;
  justification: string;
  category: string;
  acquisitionType: PurchaseAcquisitionType;
  priority: PurchasePriority;
  estimatedValue?: number;
  neededBy?: string;
  scope: string;
  technicalRequirements?: string;
  functionalRequirements?: string;
  criticality: PurchaseCriticality;
  budgetStatus?: string;
  impact?: string;
  accessNeeds: PurchaseAccessNeeds;
  items: CreatePurchaseRequestItemPayload[];
}

export interface CreateQuotationPayload {
  purchaseRequestId: string;
  supplierId: string;
  currency: string;
  proposalValidUntil: string;
  deliveryDays?: number;
  paymentTerms: string;
  commercialNotes?: string;
  technicalNotes?: string;
  taxesAmount?: number;
  freightAmount?: number;
  notes?: string;
  items: Array<{
    purchaseRequestItemId: string;
    quantity: number;
    unitPrice: number;
    deliveryDays?: number;
    notes?: string;
    comparable: boolean;
    nonComparableReason?: string;
  }>;
}

export interface EqualizationOffer {
  quotationId: string;
  supplierId: string;
  supplierName?: string;
  quantity?: number;
  unitPrice?: number | string;
  totalValue: number | string;
  deliveryDays?: number | null;
  comparable: boolean;
  nonComparableReason?: string | null;
  isLowest: boolean;
}

export interface EqualizationRow {
  requestItem: PurchaseRequestItem;
  offers: EqualizationOffer[];
  excludedOffers?: Array<{
    quotationId: string;
    supplierId: string;
    supplierName?: string;
    reason?: string | null;
  }>;
  lowestValue?: number | string | null;
}

export interface EqualizationCurrencyGroup {
  currency: string;
  validQuotationCount?: number;
  distinctSupplierCount?: number;
  requirementSatisfied?: boolean;
  matrix: EqualizationRow[];
  global: Array<{
    quotationId: string;
    supplierId: string;
    supplierName?: string;
    totalValue: number | string;
    isLowest: boolean;
  }>;
  lowestQuotationId?: string | null;
}

export interface EqualizationComparison {
  purchaseRequestId: string;
  currency?: string;
  minimumQuotes: number;
  validQuotationCount: number;
  exception?: PurchaseQuotationException | null;
  matrix?: EqualizationRow[];
  global?: EqualizationCurrencyGroup['global'];
  lowestQuotationId?: string | null;
  currencyGroups?: EqualizationCurrencyGroup[];
}

export interface PurchaseDashboard {
  totalRequests?: number;
  pendingApprovals?: number;
  quotationExceptions?: number;
  averageCycleHours?: number | string | null;
  estimatedSavings?: number | string | null;
  estimatedSavingsCurrency?: string;
  contractedSuppliers?: number;
  pendingPayments?: number;
  overduePayments?: number;
  byStatus?: Array<{ status: string; count: number }> | Record<string, number>;
  cycleTime?: Array<{ label: string; value: number }>;
  savings?: Array<{ label: string; value: number }>;
  suppliers?: Array<{ label: string; value: number }>;
  payments?: Array<{ label: string; value: number }>;
}

export interface PurchaseListParams {
  status?: PurchaseRequestStatus | '';
  search?: string;
  requesterUserId?: string;
  limit?: number;
  offset?: number;
}

function query(params?: Record<string, unknown>): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== '') result[key] = String(value);
  }
  return result;
}

export function purchaseErrorMessage(error: unknown): string {
  if (!axios.isAxiosError(error)) return error instanceof Error ? error.message : 'Não foi possível concluir a operação.';
  const body = error.response?.data as { message?: string | string[]; error?: string } | undefined;
  if (Array.isArray(body?.message)) return body.message.join(' ');
  return body?.message ?? body?.error ?? error.message ?? 'Não foi possível concluir a operação.';
}

export async function listPurchaseRequests(params?: PurchaseListParams): Promise<Paginated<PurchaseRequest>> {
  const { data } = await api.get('/purchase-requests', { params: query(params as Record<string, unknown>) });
  return unwrapPaginated<PurchaseRequest>(data);
}

export async function getPurchaseRequest(id: string): Promise<PurchaseRequest> {
  const { data } = await api.get(`/purchase-requests/${id}`);
  return unwrapObject<PurchaseRequest>(data);
}

export async function createPurchaseRequest(payload: CreatePurchaseRequestPayload): Promise<PurchaseRequest> {
  const { data } = await api.post('/purchase-requests', payload);
  return unwrapObject<PurchaseRequest>(data);
}

export async function updatePurchaseRequest(
  id: string,
  payload: CreatePurchaseRequestPayload,
): Promise<PurchaseRequest> {
  const { data } = await api.patch(`/purchase-requests/${id}`, payload);
  return unwrapObject<PurchaseRequest>(data);
}

export async function uploadPurchaseAttachment(
  requestId: string,
  input: { file: File; kind?: string; description?: string },
): Promise<PurchaseRequestAttachment> {
  const form = new FormData();
  form.append('file', input.file);
  if (input.kind) form.append('kind', input.kind);
  if (input.description) form.append('description', input.description);
  const { data } = await api.post(`/purchase-requests/${requestId}/attachments`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrapObject<PurchaseRequestAttachment>(data);
}

export async function deletePurchaseAttachment(requestId: string, attachmentId: string): Promise<void> {
  await api.delete(`/purchase-requests/${requestId}/attachments/${attachmentId}`);
}

export async function submitPurchaseRequest(id: string): Promise<PurchaseRequest> {
  const { data } = await api.post(`/purchase-requests/${id}/submit`, {});
  return unwrapCommandRequest(data);
}

export async function decidePurchaseRequest(
  id: string,
  payload: { decision: 'approve' | 'reject' | 'return'; stepKey?: string; justification?: string },
): Promise<PurchaseRequest> {
  const { data } = await api.post(`/purchase-requests/${id}/decisions`, payload);
  return unwrapCommandRequest(data);
}

export async function selectPurchaseQuotation(
  id: string,
  payload: { quotationId: string; justification?: string },
): Promise<PurchaseRequest> {
  const { data } = await api.post(`/purchase-requests/${id}/selection`, payload);
  return unwrapCommandRequest(data);
}

export async function closePurchaseRequest(id: string): Promise<PurchaseRequest> {
  const { data } = await api.post(`/purchase-requests/${id}/close`, {});
  return unwrapCommandRequest(data);
}

export async function listPurchaseQuotations(requestId: string): Promise<PurchaseQuotation[]> {
  const { data } = await api.get('/purchase-quotations', { params: { purchaseRequestId: requestId, limit: 100 } });
  return unwrapArray<PurchaseQuotation>(data);
}

export async function createPurchaseQuotation(payload: CreateQuotationPayload): Promise<PurchaseQuotation> {
  const { data } = await api.post('/purchase-quotations', payload);
  return unwrapObject<PurchaseQuotation>(data);
}

export async function uploadQuotationProposal(id: string, file: File): Promise<PurchaseQuotation> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post(`/purchase-quotations/${id}/proposal`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrapObject<PurchaseQuotation>(data);
}

export async function submitPurchaseQuotation(id: string): Promise<PurchaseQuotation> {
  const { data } = await api.post(`/purchase-quotations/${id}/submit`, {});
  return unwrapObject<PurchaseQuotation>(data);
}

export async function createQuotationException(
  requestId: string,
  payload: { reason: string; justification: string },
): Promise<PurchaseQuotationException> {
  const { data } = await api.post(`/purchase-requests/${requestId}/quotation-exception`, payload);
  return unwrapObject<PurchaseQuotationException>(data);
}

export async function comparePurchaseQuotations(requestId: string): Promise<EqualizationComparison> {
  const { data } = await api.get('/purchase-equalizations/compare', { params: { purchaseRequestId: requestId } });
  return unwrapObject<EqualizationComparison>(data);
}

export async function listPurchaseApprovals(requestId: string): Promise<PurchaseApproval[]> {
  const { data } = await api.get(`/purchase-requests/${requestId}/approvals`);
  return unwrapArray<PurchaseApproval>(data);
}

export async function listPurchaseTimeline(requestId: string): Promise<PurchaseTimelineEntry[]> {
  const { data } = await api.get(`/purchase-requests/${requestId}/timeline`);
  return unwrapArray<PurchaseTimelineEntry>(data);
}

export async function listApprovalQueue(): Promise<Paginated<PurchaseApprovalQueueEntry>> {
  const { data } = await api.get('/purchase-approvals/queue', { params: { limit: 100 } });
  return unwrapPaginated<PurchaseApprovalQueueEntry>(data);
}

export async function listPurchasePayments(requestId: string): Promise<PurchasePayment[]> {
  const { data } = await api.get('/purchase-payments', { params: { purchaseRequestId: requestId } });
  return unwrapArray<PurchasePayment>(data);
}

export async function createPurchasePayment(payload: {
  purchaseRequestId: string;
  dueDate: string;
  amount?: number;
  idempotencyKey: string;
}): Promise<PurchasePayment> {
  const { data } = await api.post('/purchase-payments', payload);
  return unwrapObject<PurchasePayment>(data);
}

export async function schedulePurchasePayment(id: string, scheduledFor: string): Promise<PurchasePayment> {
  const { data } = await api.post(`/purchase-payments/${id}/schedule`, { scheduledFor });
  return unwrapObject<PurchasePayment>(data);
}

export async function markPurchasePaymentPaid(
  id: string,
  payload: { paymentReference: string; paidAt?: string },
): Promise<PurchasePayment> {
  const { data } = await api.post(`/purchase-payments/${id}/mark-paid`, payload);
  return unwrapObject<PurchasePayment>(data);
}

export async function reconcilePurchasePayment(
  id: string,
  payload: { reconciliationReference: string; reconciledAt?: string },
): Promise<PurchasePayment> {
  const { data } = await api.post(`/purchase-payments/${id}/reconcile`, payload);
  return unwrapObject<PurchasePayment>(data);
}

export async function cancelPurchasePayment(id: string, justification: string): Promise<PurchasePayment> {
  const { data } = await api.post(`/purchase-payments/${id}/cancel`, { justification });
  return unwrapObject<PurchasePayment>(data);
}

export async function uploadPaymentProof(id: string, file: File): Promise<PurchasePayment> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post(`/purchase-payments/${id}/proof`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrapObject<PurchasePayment>(data);
}

export async function getPurchaseDashboard(): Promise<PurchaseDashboard> {
  const { data } = await api.get('/purchase-reports/dashboard');
  return unwrapObject<PurchaseDashboard>(data);
}

export async function listCostCenters(): Promise<NamedReference[]> {
  const { data } = await api.get('/purchase-catalogs/cost-centers');
  return unwrapArray<NamedReference>(data);
}

export async function listProjects(): Promise<NamedReference[]> {
  const { data } = await api.get('/purchase-catalogs/projects');
  return unwrapArray<NamedReference>(data);
}

export async function listClients(): Promise<NamedReference[]> {
  const { data } = await api.get('/purchase-catalogs/clients');
  return unwrapArray<NamedReference>(data);
}

export async function listEligibleSuppliers(): Promise<NamedReference[]> {
  const { data } = await api.get('/suppliers', { params: { limit: 100 } });
  return unwrapArray<NamedReference>(data).filter((supplier) => supplier.purchaseEligible === true);
}

function unwrapCommandRequest(body: unknown): PurchaseRequest {
  const payload = unwrapObject<Record<string, unknown>>(body);
  return ((payload.request && typeof payload.request === 'object') ? payload.request : payload) as unknown as PurchaseRequest;
}

function fileNameFromDisposition(disposition: string | undefined, fallback: string): string {
  const encoded = disposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const plain = disposition?.match(/filename="?([^";]+)"?/i)?.[1];
  const value = encoded ?? plain;
  if (!value) return fallback;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

async function authenticatedDownload(path: string, fallback: string): Promise<void> {
  const response = await api.get(path, { responseType: 'blob' });
  const objectUrl = URL.createObjectURL(response.data as Blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileNameFromDisposition(response.headers['content-disposition'], fallback);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
}

export const downloadPurchaseAttachment = (requestId: string, attachmentId: string, fallback: string) =>
  authenticatedDownload(`/purchase-requests/${requestId}/attachments/${attachmentId}`, fallback);

export const downloadQuotationProposal = (quotationId: string) =>
  authenticatedDownload(`/purchase-quotations/${quotationId}/proposal`, 'proposta');

export const downloadPaymentProof = (paymentId: string) =>
  authenticatedDownload(`/purchase-payments/${paymentId}/proof`, 'comprovante');
